// Mode Jour J (Prompt 7 §7) — écran terrain mobile pour la tournée du jour.
// Route /jour-j/:date. Actions : arrivée, absent, soin terminé (+ encaissement).
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Navigation, Phone, MessageSquare, KeyRound, ArrowLeft, CheckCircle } from "lucide-react";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const frLong = (d) => { const dt = new Date(d); return isNaN(dt) ? d : cap(dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })); };
const eur = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " €";

export default function JourJ() {
  const { date } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pay, setPay] = useState(null); // { rdv_id }
  const [moyen, setMoyen] = useState("cb");
  const [montant, setMontant] = useState("");

  const load = () => api.get(`/jour-j/${date}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const act = async (rdv_id, action, extra = {}) => {
    try { await api.post(`/jour-j/visite/${rdv_id}`, { action, ...extra }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const encaisser = async () => { await act(pay.rdv_id, "termine", { moyen, montant: parseFloat(montant) || 0 }); setPay(null); setMontant(""); };

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f2ec" }}><span className="text-slate-400">Chargement…</span></div>;
  const visites = data.visites || [];
  const cur = visites.find((v) => !["Soin délivré", "Absent"].includes(v.statut));
  const idx = cur ? visites.indexOf(cur) : visites.length;
  const done = visites.filter((v) => ["Soin délivré", "Absent"].includes(v.statut)).length;

  return (
    <div className="min-h-screen" style={{ background: "#f4f2ec" }}>
      <div className="mx-auto" style={{ maxWidth: 430 }}>
        <div className="px-4 pt-4 pb-3">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[13px] mb-2" style={{ color: "#6b7180" }}><ArrowLeft className="w-4 h-4" />Retour</button>
          <div className="text-[14px]" style={{ color: "#4a5060" }}>Tournée {data.titre}</div>
          <div className="text-[30px] font-bold font-display" style={{ color: "#141821" }}>{frLong(date)}</div>
          <div className="text-[16px] mt-0.5" style={{ color: "#4a5060" }}>{cur ? `Visite ${idx + 1} sur ${visites.length}` : `${done}/${visites.length} visites terminées`}</div>
          <div className="flex gap-1 mt-2">{visites.map((v, i) => <div key={i} className="flex-1 rounded-full" style={{ height: 6, background: ["Soin délivré", "Absent"].includes(v.statut) ? "#141821" : (v === cur ? "#facc15" : "#dcd8cc") }} />)}</div>
        </div>

        {cur ? (
          <div className="mx-4 bg-white p-[18px]" style={{ borderRadius: 22 }} data-testid="jj-current">
            <div className="flex items-baseline gap-2"><span className="mono text-[17px] font-semibold">{cur.heure}</span></div>
            <div className="text-[28px] font-bold font-display mt-1" style={{ color: "#141821" }}>{cur.patient_nom}</div>
            <div className="text-[18px]" style={{ color: "#4a5060" }}>{cur.adresse || "Adresse manquante"}</div>
            {(cur.code_acces || cur.indication) && <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-xl text-[17px] font-bold" style={{ background: "oklch(0.96 0.05 95)", color: "#141821" }}><KeyRound className="w-5 h-5" />{[cur.code_acces, cur.indication].filter(Boolean).join(" · ")}</div>}
            {cur.montant ? <div className="text-[15px] mt-2" style={{ color: "#4a5060" }}>{eur(cur.montant)}</div> : null}
            <div className="flex gap-2 mt-4">
              <a href={cur.adresse ? `https://maps.google.com/?q=${encodeURIComponent(cur.adresse)}` : "#"} target="_blank" rel="noreferrer" data-testid="jj-itineraire" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl text-white font-medium text-[15px]" style={{ height: 56, background: "#141821" }}><Navigation className="w-5 h-5" />Itinéraire</a>
              {cur.telephone && <a href={`tel:${cur.telephone}`} className="inline-flex items-center justify-center rounded-xl" style={{ width: 56, height: 56, border: "1px solid #d6d8de", background: "#fff" }}><Phone className="w-5 h-5" /></a>}
              {cur.telephone && <a href={`sms:${cur.telephone}?body=${encodeURIComponent("J'arrive dans 10 min")}`} className="inline-flex items-center justify-center rounded-xl" style={{ width: 56, height: 56, border: "1px solid #d6d8de", background: "#fff" }}><MessageSquare className="w-5 h-5" /></a>}
            </div>
          </div>
        ) : (
          <div className="mx-4 bg-white p-[18px] text-center" style={{ borderRadius: 22 }} data-testid="jj-termine">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <div className="text-[20px] font-bold font-display">Terminer la journée</div>
            <div className="text-[14px] mt-2" style={{ color: "#4a5060" }}>{done} visite{done > 1 ? "s" : ""} · {visites.filter((v) => v.statut === "Absent").length} absent(s)</div>
            <button data-testid="jj-bilan" onClick={() => { const t = document.referrer; nav("/tournees"); }} className="mt-4 w-full rounded-xl font-semibold" style={{ height: 52, background: "#facc15", color: "#141821" }}>Voir le bilan</button>
          </div>
        )}

        {/* Ensuite */}
        {cur && (
          <div className="px-4 mt-4">
            <div className="text-[13px] font-semibold mb-1" style={{ color: "#6b7180" }}>Ensuite</div>
            {visites.filter((v) => v !== cur && !["Soin délivré", "Absent"].includes(v.statut)).map((v) => (
              <div key={v.rdv_id} className="flex items-center gap-3" style={{ height: 60, borderBottom: "1px solid #e3ded2" }}>
                <span className="mono text-[15px]" style={{ color: "#6b7180" }}>{v.heure}</span>
                <span className="text-[17px] flex-1 truncate" style={{ color: "#141821" }}>{v.patient_nom}</span>
              </div>
            ))}
          </div>
        )}

        {/* Barre basse */}
        {cur && (
          <div className="sticky bottom-0 mt-4 p-3 flex gap-2" style={{ background: "#f4f2ec", borderTop: "1px solid #e3ded2" }}>
            {cur.statut !== "Arrivé" ? (
              <>
                <button data-testid="jj-absent" onClick={() => act(cur.rdv_id, "absent")} className="flex-1 rounded-xl font-medium text-[15px]" style={{ height: 60, background: "#fff", border: "1px solid #d6d8de" }}>Absent</button>
                <button data-testid="jj-arrive" onClick={() => act(cur.rdv_id, "arrivee")} className="flex-1 rounded-xl font-medium text-[15px]" style={{ height: 60, background: "#facc15", color: "#141821" }}>Je suis arrivé</button>
              </>
            ) : (
              <>
                {cur.soin_id && <button onClick={() => nav(`/soins/${cur.soin_id}`)} className="flex-1 rounded-xl font-medium text-[15px]" style={{ height: 60, background: "#fff", border: "1px solid #d6d8de" }}>Fiche de soin</button>}
                <button data-testid="jj-termine-btn" onClick={() => { setPay({ rdv_id: cur.rdv_id }); setMontant(String(cur.montant || "")); }} className="flex-1 rounded-xl font-medium text-[15px]" style={{ height: 60, background: "#facc15", color: "#141821" }}>Soin terminé</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Encaissement */}
      {pay && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: "rgba(0,0,0,.4)" }} onClick={() => setPay(null)}>
          <div className="w-full bg-white p-4 rounded-t-2xl" style={{ maxWidth: 430 }} onClick={(e) => e.stopPropagation()}>
            <div className="text-[17px] font-bold mb-3">Encaissement</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[["especes", "Espèces"], ["cb", "CB"], ["lien", "Lien de paiement"], ["plus_tard", "Plus tard"]].map(([k, l]) => <button key={k} data-testid={`jj-moyen-${k}`} onClick={() => setMoyen(k)} className="rounded-xl text-[14px]" style={{ height: 48, border: moyen === k ? "2px solid #141821" : "1px solid #d6d8de" }}>{l}</button>)}
            </div>
            {moyen !== "plus_tard" && <input data-testid="jj-montant" type="number" value={montant} onChange={(e) => setMontant(e.target.value)} placeholder="Montant €" className="w-full rounded-xl px-3 mb-3" style={{ height: 48, border: "1px solid #d6d8de" }} />}
            <button data-testid="jj-encaisser" onClick={() => moyen === "plus_tard" ? act(pay.rdv_id, "termine").then(() => setPay(null)) : encaisser()} className="w-full rounded-xl font-semibold" style={{ height: 52, background: "#141821", color: "#fff" }}>Valider et visite suivante</button>
          </div>
        </div>
      )}
    </div>
  );
}
